import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { wsUrl } from "@/lib/api";
import { toast } from "sonner";
import UnoGame from "@/pages/Game";
import SkipBoGame from "@/pages/SkipBoGame";

const MAX_RETRIES = 6;

export default function GameHub() {
  const { roomId } = useParams();
  const nav = useNavigate();
  const { user, sessionToken } = useAuth();
  const [state, setState] = useState(null);
  const [wsReady, setWsReady] = useState(false);
  const [lastReaction, setLastReaction] = useState(null);
  const [attempt, setAttempt] = useState(0);
  const [fatal, setFatal] = useState(null);
  const wsRef = useRef(null);
  const retryTimerRef = useRef(null);
  const mountedRef = useRef(true);
  const attemptRef = useRef(0);

  useEffect(() => {
    mountedRef.current = true;
    attemptRef.current = 0;
    const token = sessionToken || localStorage.getItem("session_token");
    if (!token) { nav("/"); return; }

    const connect = () => {
      if (!mountedRef.current) return;
      // Close any previous socket to avoid StrictMode double-open
      try { wsRef.current?.close(); } catch (e) {}

      const url = wsUrl(roomId, token);
      const ws = new WebSocket(url);
      wsRef.current = ws;

      ws.onopen = () => {
        if (!mountedRef.current) { try { ws.close(); } catch(e){} return; }
        attemptRef.current = 0;
        setAttempt(0);
        setWsReady(true);
        setFatal(null);
      };
      ws.onmessage = (ev) => {
        if (!mountedRef.current) return;
        try {
          const data = JSON.parse(ev.data);
          if (data.type === "state") {
            setState({ ...data.state, chat: data.chat || [] });
          } else if (data.type === "error") {
            toast.error(data.message);
          } else if (data.type === "quick_reaction") {
            setLastReaction({ ...data.reaction, id: Math.random().toString(36).slice(2) });
          }
        } catch (e) {}
      };
      ws.onclose = (ev) => {
        if (!mountedRef.current) return;
        setWsReady(false);
        // 4404 = room gone, 4401 = unauthorized → don't retry
        if (ev.code === 4404) {
          setFatal("Room no longer exists.");
          return;
        }
        if (ev.code === 4401) {
          setFatal("You need to sign in again.");
          return;
        }
        // Retry with backoff
        if (attemptRef.current < MAX_RETRIES) {
          const wait = Math.min(1500 * Math.pow(1.5, attemptRef.current), 6000);
          attemptRef.current += 1;
          setAttempt(attemptRef.current);
          retryTimerRef.current = setTimeout(connect, wait);
        } else {
          setFatal("Could not reach the table. Try leaving and rejoining.");
        }
      };
      ws.onerror = () => {
        // onclose will fire and handle retry
      };
    };

    connect();

    return () => {
      mountedRef.current = false;
      if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
      try { wsRef.current?.close(); } catch (e) {}
    };
  }, [roomId, sessionToken, nav]);

  const send = (msg) => {
    const ws = wsRef.current;
    if (ws && ws.readyState === 1) ws.send(JSON.stringify(msg));
    else toast.error("Reconnecting…");
  };

  // Block iOS Safari's left-edge back-swipe gesture from kicking the user out
  // of the game. Add an extra history entry, and if the user pops back
  // (accidental swipe), push a new one so they stay in the room. The explicit
  // "Leave" button uses nav("/lobby") which still works normally.
  useEffect(() => {
    if (typeof window === "undefined") return;
    window.history.pushState({ dosGameGuard: true }, "");
    const onPop = () => {
      if (!mountedRef.current) return;
      window.history.pushState({ dosGameGuard: true }, "");
      toast("Use the Leave button to exit the game", { duration: 2500 });
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, [roomId]);

  if (fatal) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <div className="glass rounded-2xl p-8 max-w-md text-center">
          <div className="text-xs uppercase tracking-[0.3em] text-white/50">Connection</div>
          <div className="font-display text-2xl mt-2">{fatal}</div>
          <button onClick={() => nav("/lobby")}
            className="mt-6 px-6 py-2.5 rounded-full bg-[color:var(--uno-red)] text-white font-semibold hover:brightness-110">
            Back to lobby
          </button>
        </div>
      </div>
    );
  }

  if (!state) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="text-white/70 font-display text-2xl">
            {attempt > 0 ? "Reconnecting…" : "Joining table…"}
          </div>
          {attempt > 0 && (
            <div className="text-xs uppercase tracking-[0.3em] text-white/40 mt-3">
              attempt {attempt} / {MAX_RETRIES}
            </div>
          )}
        </div>
      </div>
    );
  }

  if (state.game_type === "skipbo") {
    return <SkipBoGame state={state} send={send} wsReady={wsReady} lastReaction={lastReaction} />;
  }
  return <UnoGame state={state} send={send} wsReady={wsReady} lastReaction={lastReaction} />;
}
